import { ISelect } from '@tecalisdev/infinity';

import { IFormOperation, IFormOperationField, IOperation, OPERATION_TYPE } from '../../operations/models/operations.models';
import { IWorkflowAuthMatchDataPayload, IWorkflowDetailResponse } from '../../workflows/models/workflows.models';

/** Tipos de documento de identidad admitidos en match_data de autorización (paridad journey PWCS). */
export const AUTH_MATCH_IDENTITY_DOCUMENT_TYPE_IDS = ['DNI', 'NIE', 'PASSPORT'] as const;

export type AuthMatchIdentityDocumentTypeId = (typeof AUTH_MATCH_IDENTITY_DOCUMENT_TYPE_IDS)[number];

export function buildAuthMatchIdentityDocumentTypeOptions(
	translateFn: (key: string) => string,
): ISelect[] {
	return AUTH_MATCH_IDENTITY_DOCUMENT_TYPE_IDS.map((id) => ({
		id,
		label: translateFn(`MASTER.DOCUMENT_TYPE.${id}`),
	}));
}

export type FormCanonicalAuthMatchDataControlName =
	| 'id_document_type'
	| 'address_number'
	| 'address_floor'
	| 'address_door';

export type AuthMatchDataControlName =
	| keyof IWorkflowAuthMatchDataPayload
	| 'support_number'
	| FormCanonicalAuthMatchDataControlName;

const FORM_FIELD_AUTH_MATCH_DATA_CONTROL: Partial<Record<string, AuthMatchDataControlName>> = {
	name: 'names',
	surname: 'surnames',
	id_document_type: 'id_document_type',
	id_document: 'identifier',
	birth_date: 'birthdate',
	address: 'address',
	address_number: 'address_number',
	address_floor: 'address_floor',
	address_door: 'address_door',
	zip_code: 'postal_code',
	city: 'municipality',
	province: 'province',
	country: 'country',
};

const AUTH_MATCH_DATA_CONTROL_ORDER: AuthMatchDataControlName[] = [
	'names',
	'surnames',
	'id_document_type',
	'identifier',
	'birthdate',
	'address',
	'address_number',
	'address_floor',
	'address_door',
	'postal_code',
	'municipality',
	'country',
	'province',
];

export function resolveFormAuthMatchDataControls(
	workflow: IWorkflowDetailResponse | null | undefined,
): AuthMatchDataControlName[] {
	if (!workflow?.operations?.length) {
		return [];
	}

	// Los campos match_data de FORM solo aplican en workflows sin OCR (el cotejo viene del formulario).
	if (hasOcrOperationInWorkflow(workflow)) {
		return [];
	}

	const controls = new Set<AuthMatchDataControlName>();
	for (const operation of listOperations(workflow.operations)) {
		if (operation.type !== OPERATION_TYPE.FORM) {
			continue;
		}
		for (const field of listFormFields(operation)) {
			const controlName = resolveFormFieldAuthMatchDataControl(field);
			if (controlName) {
				controls.add(controlName);
			}
		}
	}

	return AUTH_MATCH_DATA_CONTROL_ORDER.filter((controlName) => controls.has(controlName));
}

function hasOcrOperationInWorkflow(workflow: IWorkflowDetailResponse): boolean {
	return listOperations(workflow.operations).some((operation) => operation.type === OPERATION_TYPE.OCR);
}

function listOperations(operations: IOperation[]): IOperation[] {
	return operations.flatMap((operation) => {
		if (operation.type === OPERATION_TYPE.CHOOSE) {
			return [operation, ...operation.config.operations];
		}
		return [operation];
	});
}

export function resolveFormFieldAuthMatchDataControl(
	field: Pick<IFormOperationField, 'id' | 'name' | 'validators'>,
): AuthMatchDataControlName | null {
	if (!field.validators?.includes('match_ocr')) {
		return null;
	}

	return FORM_FIELD_AUTH_MATCH_DATA_CONTROL[field.id] ?? FORM_FIELD_AUTH_MATCH_DATA_CONTROL[field.name] ?? null;
}

function listFormFields(operation: IFormOperation): IFormOperationField[] {
	return operation.config.steps.flatMap((step) => step.fields);
}
